import produce from 'immer';
import { merge } from 'lodash';

import { StorageKeys } from 'types/index';

import initialState from './initialState';
import {
	Actions,
	ActionTypes,
	State,
	UserLicenseModel,
	SubscriptionStatus,
	UserStatus,
	SubscriptionUsersById,
	SubscriptionName
} from './types';
import { ActionTypes as EnterpriseActionTypes } from '../enterprise/types';
import { Actions as EnterpriseActions } from '../enterprise';

import { ActionTypes as PaymentsActionTypes, Actions as PaymentsActions } from '../payments';

export default (
	state: State = initialState,
	action: Actions | PaymentsActions | EnterpriseActions
): State => {
	switch (action.type) {
		case ActionTypes.GET_ACCOUNT: {
			const { account } = action.payload;

			return produce(state, draft => {
				draft.accountDetails = account;
				// TODO: remove all particular logic for userPreferences when all DB data is scripted NO->NB.
				const chosenLanguageCode = account.userPreferences.chosenLanguageCode;
				if (chosenLanguageCode && draft.accountDetails.userPreferences) {
					draft.accountDetails.userPreferences.chosenLanguageCode = chosenLanguageCode;
				}
			});
		}
		case ActionTypes.UPDATE_ACCOUNT: {
			const { accountDetails } = action.payload;

			return produce(state, draft => {
				draft.accountDetails = merge(draft.accountDetails, accountDetails);
			});
		}

		case ActionTypes.GET_SUBSCRIPTION: {
			return produce(state, draft => {
				const {
					subscriptionDetails,
					subscriptionUsersById,
					isNonSubscriber,
					hasMetadataDefinition
				} = action.payload;
				draft.subscriptionDetails = subscriptionDetails;
				draft.subscriptionUsers.byId = subscriptionUsersById;
				draft.isNonSubscriber = isNonSubscriber;
				draft.hasMetadataDefinition = hasMetadataDefinition;

				const storageSubscriptionStatus = localStorage.getItem(
					StorageKeys.SubscriptionUpdating
				);
				if (storageSubscriptionStatus === 'true' && isNonSubscriber)
					draft.subscriptionStatus = SubscriptionStatus.ChangePlanInProgress;
				else {
					Object.values(subscriptionUsersById).forEach(user => {
						if (user.isOwner) draft.subscriptionStatus = user.subscriptionStatus;
					});
				}
			});
		}

		case ActionTypes.ADD_SUBSCRIPTION_USER: {
			const { user } = action.payload;

			return produce(state, draft => {
				const { licenseModel, ...userToAdd } = user;
				draft.subscriptionUsers.byId[user.userid] = {
					...userToAdd,
					licenseModel
				};
				// allow backwards compatibility for both availableLicenseQuantity and availableLicenseQuantities
				if (
					draft.subscriptionDetails &&
					draft.subscriptionDetails.availableLicenseQuantity
				) {
					draft.subscriptionDetails.availableLicenseQuantity =
						draft.subscriptionDetails.availableLicenseQuantity - 1;
				}

				// bundle version
				if (
					draft.subscriptionDetails &&
					draft.subscriptionDetails.availableLicenseQuantities &&
					draft.subscriptionDetails.availableLicenseQuantities[licenseModel]
				) {
					draft.subscriptionDetails.availableLicenseQuantities[licenseModel] =
						draft.subscriptionDetails.availableLicenseQuantities[licenseModel] - 1;
				}
			});
		}

		case EnterpriseActionTypes.UPDATE_ENTERPRISE_USER: {
			const { user } = action.payload;

			return produce(state, draft => {
				draft.subscriptionUsers.byId[user.userid] = user;
			});
		}

		case ActionTypes.CANCEL_SUBSCRIPTION_USER_INVITATION: {
			const { userId, licenseModel } = action.payload;

			return produce(state, draft => {
				delete draft.subscriptionUsers.byId[userId];
				if (
					draft.subscriptionDetails &&
					draft.subscriptionDetails.availableLicenseQuantity
				) {
					draft.subscriptionDetails.availableLicenseQuantity =
						draft.subscriptionDetails.availableLicenseQuantity + 1;
				}

				// bundle version
				if (
					draft.subscriptionDetails &&
					draft.subscriptionDetails.availableLicenseQuantities &&
					draft.subscriptionDetails.availableLicenseQuantities[licenseModel]
				) {
					draft.subscriptionDetails.availableLicenseQuantities[licenseModel] =
						draft.subscriptionDetails.availableLicenseQuantities[licenseModel] + 1;
				}
			});
		}

		case ActionTypes.REMOVE_SUBSCRIPTION_USER: {
			const { userId, licenseModel } = action.payload;

			return produce(state, draft => {
				delete draft.subscriptionUsers.byId[userId];
				if (
					draft.subscriptionDetails &&
					draft.subscriptionDetails.availableLicenseQuantity
				) {
					draft.subscriptionDetails.availableLicenseQuantity =
						draft.subscriptionDetails.availableLicenseQuantity + 1;
				}

				// bundle version
				if (
					draft.subscriptionDetails &&
					draft.subscriptionDetails.availableLicenseQuantities &&
					draft.subscriptionDetails.availableLicenseQuantities[licenseModel]
				) {
					draft.subscriptionDetails.availableLicenseQuantities[licenseModel] =
						draft.subscriptionDetails.availableLicenseQuantities[licenseModel] + 1;
				}
			});
		}

		case EnterpriseActionTypes.DEACTIVATE_USER_ACCOUNT: {
			const { userId } = action.payload;
			return produce(state, draft => {
				const computedUsers: SubscriptionUsersById = {};

				Object.values(draft.subscriptionUsers.byId).filter(user =>
					user.userid === userId
						? (computedUsers[userId] = {
								...user,
								status: UserStatus.Inactive,
								licenseModel: UserLicenseModel.NoLicense,
								subscriptionStatus: SubscriptionStatus.Inactive
						  })
						: (computedUsers[user.userid] = user)
				);

				draft.subscriptionUsers.byId = computedUsers;
				if (
					state.subscriptionDetails?.availableLicenseQuantity &&
					draft.subscriptionDetails
				) {
					draft.subscriptionDetails.availableLicenseQuantity =
						state.subscriptionDetails?.availableLicenseQuantity + 1;
				}
			});
		}

		case EnterpriseActionTypes.REACTIVATE_USER_ACCOUNT: {
			const { userId } = action.payload;
			return produce(state, draft => {
				const computedUsers: SubscriptionUsersById = {};
				Object.values(draft.subscriptionUsers.byId).filter(user =>
					user.userid === userId
						? (computedUsers[userId] = {
								...user,
								status: UserStatus.Active,
								licenseModel: UserLicenseModel.Full,
								subscriptionStatus: SubscriptionStatus.Active
						  })
						: (computedUsers[user.userid] = user)
				);
				draft.subscriptionUsers.byId = computedUsers;

				draft.subscriptionUsers.byId = computedUsers;
				if (
					state.subscriptionDetails?.availableLicenseQuantity &&
					draft.subscriptionDetails
				) {
					draft.subscriptionDetails.availableLicenseQuantity =
						state.subscriptionDetails?.availableLicenseQuantity - 1;
				}
			});
		}

		case ActionTypes.CHANGE_USER_LICENSE_MODEL: {
			const { userId, licenseModel } = action.payload;

			return produce(state, draft => {
				draft.subscriptionUsers.byId[userId].licenseModel = licenseModel;
				// backwards compatibility
				if (
					(licenseModel === UserLicenseModel.Full ||
						licenseModel === UserLicenseModel.Collaborator) &&
					draft.subscriptionDetails &&
					draft.subscriptionDetails.availableLicenseQuantity
				) {
					draft.subscriptionDetails.availableLicenseQuantity =
						draft.subscriptionDetails.availableLicenseQuantity - 1;
				}
				if (
					licenseModel === UserLicenseModel.OneOwnedOneShared &&
					draft.subscriptionDetails &&
					draft.subscriptionDetails.availableLicenseQuantity
				) {
					draft.subscriptionDetails.availableLicenseQuantity =
						draft.subscriptionDetails.availableLicenseQuantity + 1;
				}

				// bundle version
				if (userId && draft.subscriptionUsers.byId[userId]) {
					const previousLicenseModel = draft.subscriptionUsers.byId[userId].licenseModel;
					if (
						draft.subscriptionDetails &&
						draft.subscriptionDetails.availableLicenseQuantities
					) {
						draft.subscriptionDetails.availableLicenseQuantities[previousLicenseModel] =
							draft.subscriptionDetails.availableLicenseQuantities[
								previousLicenseModel
							] + 1;
						if (licenseModel !== UserLicenseModel.OneOwnedOneShared) {
							draft.subscriptionDetails.availableLicenseQuantities[licenseModel] =
								draft.subscriptionDetails.availableLicenseQuantities[licenseModel] -
								1;
						}
					}
				}

				// if user is changing his own license, update account details and
				// the other tabs with the new license model
				if (draft.accountDetails && userId === draft.accountDetails.userid) {
					draft.accountDetails.licenseModel = licenseModel;
					// temporary till we get a notification for this event
					localStorage.setItem(
						StorageKeys.LicenseModelSelfAssigned,
						licenseModel.toString()
					);
				}
			});
		}

		case ActionTypes.GET_LICENSE_MODELS: {
			const { licenseModels } = action.payload;

			return produce(state, draft => {
				draft.licenseModels = {
					fetched: true,
					...licenseModels
				};
			});
		}

		case ActionTypes.SET_SUBSCRIPTION_USERS_SEARCH_TERM: {
			const { term } = action.payload;

			return produce(state, draft => {
				draft.subscriptionUsers.searchTerm = term;
			});
		}

		case ActionTypes.GET_SUBSCRIPTION_API_KEY: {
			const { apiKey } = action.payload;

			return produce(state, draft => {
				draft.subscriptionApiKey = apiKey;
			});
		}

		case PaymentsActionTypes.CREATE_CUSTOMER_SUBSCRIPTION:
		case PaymentsActionTypes.UPDATE_CUSTOMER_SUBSCRIPTION: {
			return produce(state, draft => {
				draft.subscriptionStatus = SubscriptionStatus.ChangePlanInProgress;
				localStorage.setItem(StorageKeys.SubscriptionUpdating, 'true');
			});
		}

		case PaymentsActionTypes.DOWNGRADE_SUBSCRIPTION_TO_FREE: {
			return produce(state, draft => {
				if (draft.subscriptionDetails) {
					draft.subscriptionDetails.subscriptionName = SubscriptionName.LedidiFreeTrial;

					draft.subscriptionStatus = SubscriptionStatus.ChangePlanInProgress;
					localStorage.setItem(StorageKeys.SubscriptionUpdating, 'true');
				}
			});
		}

		default: {
			return state;
		}
	}
};
